<template>
    <div>
        <v-row class="px-2 pt-2">
            <!-- Merchants -->
            <v-col md="4" class="py-0">
                <v-autocomplete
                    v-model="merchant"
                    label="Select a merchant"
                    :items="merchants"
                    item-value="uuid"
                    item-text="name"
                    dense
                    clearable
                    @click="getReport();"
                    @click:clear="clearMerchant()"
                ></v-autocomplete>
            </v-col>

            <!-- BIN -->
            <v-col md="2" class="py-0">
                <v-text-field
                    v-model="bin"
                    append-icon="mdi-magnify"
                    label="BIN"
                    clearable
                    @keyup.enter="getReport();"
                    @click:clear="bin = null; getReport();"
                    class="py-0"
                ></v-text-field>
            </v-col>

            <!-- From Time -->
            <v-col md="2" class="py-0">
                <v-menu
                    ref="fromMenu"
                    v-model="fromTimeModal"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    :return-value.sync="fromTime"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                >
                    <template v-slot:activator="{ on }">
                        <v-text-field
                            v-model="fromTime"
                            label="From"
                            append-icon="mdi-clock-outline"
                            readonly
                            class="py-0"
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-time-picker
                    v-if="fromTimeModal"
                    v-model="fromTime"
                    full-width
                    ampm-in-title
                    :allowed-minutes="allowedStep"
                    format="24hr"
                    @click:minute="$refs.fromMenu.save(fromTime); handleTimeFilters();"
                    ></v-time-picker>
                </v-menu>
            </v-col>

            <!-- To Time -->
            <v-col md="2" class="py-0">
                <v-menu
                    ref="toMenu"
                    v-model="toTimeModal"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    :return-value.sync="toTime"
                    transition="scale-transition"
                    offset-y
                    max-width="290px"
                    min-width="290px"
                >
                    <template v-slot:activator="{ on }">
                        <v-text-field
                            v-model="toTime"
                            label="To"
                            append-icon="mdi-clock-outline"
                            readonly
                            class="py-0"
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-time-picker
                    v-if="toTimeModal"
                    v-model="toTime"
                    full-width
                    ampm-in-title
                    :allowed-minutes="allowedStep"
                    format="24hr"
                    @click:minute="$refs.toMenu.save(toTime); handleTimeFilters();"
                    ></v-time-picker>
                </v-menu>
            </v-col>

            <!-- Clearing Institute -->
            <v-col md="2" class="py-0">
                <v-select
                    v-model="clearingInstitute"
                    dense
                    clearable
                    :items="clearingInstitutes"
                    item-text="name"
                    item-value="name"
                    label="Clearing Institutes"
                    append-icon="mdi-magnify"
                    @change="getReport"
                ></v-select>
            </v-col>

            <!-- Recurring Type -->
            <v-col md="2">
                <v-select
                    v-model="recurringType"
                    dense
                    :items="recurringTypes"
                    label="Recurring Type"
                    append-icon="mdi-magnify"
                    @change="getReport"
                ></v-select>
            </v-col>

            <!-- 3D Secure -->
            <v-col md="2">
                <v-select
                    v-model="threeDSecure"
                    :items="threeDSecureOptions"
                    dense
                    label="3D Secure"
                    append-icon="mdi-magnify"
                    @change="getReport"
                ></v-select>
            </v-col>

            <!-- Chart or Table -->
            <v-col md="8" align="end">
                <v-btn-toggle
                    v-model="toggle"
                    mandatory
                    color="primary"
                    dense
                    borderless>
                    <v-btn>
                        <v-icon>mdi-table</v-icon>
                    </v-btn>
                    <v-btn :disabled="!chartBtn">
                        <v-icon>mdi-chart-bar</v-icon>
                    </v-btn>
                </v-btn-toggle>
            </v-col>
        </v-row>

        <v-row class="px-4">
            <v-col md="12" class="pa-2" v-show="toggle == 0">
                <v-data-table
                    v-if="report"
                    :headers="headers"
                    :items="report"
                    :items-per-page="10"
                    class="elevation-0"
                >
                    <template v-slot:item.threeDSecure="{ item }">
                        {{ item.threeDSecure == '1' ? 'Yes' : 'No' }}
                    </template>

                    <template v-slot:item.approvalRate="{ item }">
                        {{ item.approvalRate }}%
                    </template>

                    <template v-slot:item.doNotHonorRatio="{ item }">
                        {{ item.doNotHonorRatio }}%
                    </template>
                </v-data-table>
            </v-col>

            <v-col md="12" class="pa-2 pt-0" v-show="toggle == 1">
                <chart
                    v-if="chartBtn && report && report.length"
                    :chartData="report"
                    ref="chart"
                ></chart>
            </v-col>
        </v-row>

        <v-overlay :value="loading">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
import Chart from '@/components/reports/charts/BinApprovalRates.vue';

export default {
    components: {
        'chart': Chart,
    },
    computed: {
        chartBtn() {
            if(this.merchant && this.bin && this.clearingInstitute && this.recurringType && this.threeDSecure !== null) {
                if(this.report.length) {
                    return true;
                }
                else {
                    return false;
                }
            }
            else {
                return false;
            }
        }
    },
    data() {
        return {
            merchant: null,
            merchants: null,
            bin: null,
            fromTimeModal: null,
            toTimeModal: null,
            fromTime: moment().subtract('15', 'minute').format('HH:00'),
            toTime: moment().format('HH:15'),
            clearingInstitute: null,
            clearingInstitutes: null,
            recurringType: null,
            recurringTypes: [{ value: null, text: "All" },
                             { value: 'NONE', text: "None" },
                             { value: "INITIAL", text: "Initial" },
                             { value: "REPEATED", text: "Repeated" }],
            threeDSecure:  null,
            threeDSecureOptions: [{ value: null, text: "All" },
                                  { value: 0, text: "Non 3D Secure" },
                                  { value: 1, text: "3D Secure" }],
            loading: true,
            report: null,
            toggle: null,
            headers: [{ text: 'From', value: 'timeFrom', sortable: true },
                      { text: 'To', value: 'timeTo', sortable: true },
                      { text: 'Acquirer', value: 'clearingInstitute', sortable: false, align: 'left' },
                      { text: 'BIN', value: 'bin', sortable: false, align: 'left' },
                      { text: 'Recurring Type', value: 'recurringType', sortable: false, align: 'left' },
                      { text: '3D Secure', value: 'threeDSecure', sortable: false, align: 'center' },
                      { text: 'Avg. Transaction Volume', value: 'avergaeTransactionVolume', sortable: true, align: 'center' },
                      { text: 'Approval Rate', value: 'approvalRate', sortable: true, align: 'center' },
                      { text: 'Do Not Honor %', value: 'doNotHonorRatio', sortable: false, align: 'center' }]
        }
    },
    beforeMount() {
        this.getMerchants();
        this.getClearingInstitutes();
        this.getReport();
    },
    methods: {
        getMerchants() {
            axios({
            method: 'get',
            url: `get-merchants-live`,
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                this.merchants = response.data;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        getReport() {
            this.loading = true;
            axios({
            method: 'post',
            url: `bin-approval-rates`,
            data: { merchant: this.merchant,
                    bin: this.bin,
                    fromTime: `${this.fromTime}:00`,
                    toTime: `${this.toTime }:00`,
                    clearingInstitute: this.clearingInstitute,
                    recurringType: this.recurringType,
                    threeDSecure: this.threeDSecure },
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                this.toggle = this.chartBtn ? 1 : 0;
                this.report = response.data;
                this.loading = false;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        getClearingInstitutes() {
            axios({
            method: 'get',
            url: 'get-clearing-institutes-live',
            config: { headers: { 'Content-Type': 'application/x-www-form-urlencoded' }}
            })
            .then(response => {
                this.clearingInstitutes = response.data;
            })
            .catch(e => {
                this.errors.push(e)
            })
        },
        handleTimeFilters() {
            let from = moment(this.fromTime, "HH:mm");
            let to = moment(this.toTime, "HH:mm");

            if(to < from) {
                to = from.add(15, 'minute');
                this.toTime = to.format('HH:mm');
            }
            this.getReport();
        },
        allowedStep: m => m % 15 === 0,
        clearMerchant() {
            console.log(this.merchant);
        }
    }
}
</script>

<style>

</style>